import { datadogRum } from '@datadog/browser-rum';
import { get as _get } from 'lodash';

/**
 * Error categories for consistent error tracking across the application
 */
export const ErrorCategories = {
  PHOTO_LOAD: 'photo-load-error',
  PHOTO_UPLOAD: 'photo-upload-error',
  PHOTO_API: 'photo-api-error',
  PHOTO_RENDERING: 'photo-rendering-error',
  API_ERROR: 'api-error',
  VALIDATION_ERROR: 'validation-error',
  NETWORK_ERROR: 'network-error',
} as const;

/**
 * Error types for specific photo-related failures
 */
export const PhotoErrorTypes = {
  RETRIEVAL_FAILED: 'photo-retrieval-failed',
  DOCUMENTS_FETCH_FAILED: 'photo-documents-fetch-failed',
  LOAD_TIMEOUT: 'photo-load-timeout',
  INVALID_MIME_TYPE: 'photo-invalid-mime-type',
  BLOB_CREATION_FAILED: 'photo-blob-creation-failed',
} as const;

/**
 * Feature areas for context tracking
 */
export const FeatureAreas = {
  POST_FNOL_PHOTOS: 'post-fnol-photos',
  CLAIM_DETAIL_PHOTOS: 'claim-detail-photos',
  PHOTO_UPLOAD: 'photo-upload',
  PHOTO_SELECTION: 'photo-selection',
} as const;

// Action names shared by more than one call site, so a Datadog query cannot miss half the sessions.
export const ActionNames = {
  CLAIMS_RETRIEVAL_ISSUE: 'claims-retrieval-issue',
} as const;

/**
 * Error severity levels
 */
export const ErrorSeverity = {
  CRITICAL: 'critical',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
} as const;

interface BaseErrorContext {
  errorType: string;
  errorCategory: string;
  featureArea: string;
  errorSeverity?: string;
  recoverable?: boolean;
  userAction?: string;
  [key: string]: any;
}

interface PhotoErrorContext extends BaseErrorContext {
  claimNumber?: string;
  photoUrl?: string;
  mimeType?: string;
  photoCount?: number;
  attemptCount?: number;
  networkStatus?: string;
}

/**
 * Centralized error tracking utility for Datadog RUM
 */
export class DatadogErrorTracker {
  /**
   * Track a photo-related error with standardized context
   */
  static trackPhotoError(error: Error | unknown, context: PhotoErrorContext): void {
    const globalContext = datadogRum.getGlobalContext();

    const enrichedContext = {
      ...context,
      sessionPhase: globalContext.sessionStatus,
      urlId: globalContext.urlId,
      afeSessionId: globalContext['afe-session-id'],
      timestamp: new Date().toISOString(),
    };

    // Remove undefined values to keep context clean
    Object.keys(enrichedContext).forEach((key) => enrichedContext[key] === undefined && delete enrichedContext[key]);

    datadogRum.addError(error, enrichedContext);
  }

  /**
   * Track a general application error with context
   */
  static trackError(error: Error | unknown, context: BaseErrorContext): void {
    const globalContext = datadogRum.getGlobalContext();

    const enrichedContext = {
      ...context,
      sessionPhase: globalContext.sessionStatus,
      urlId: globalContext.urlId,
      afeSessionId: globalContext['afe-session-id'],
      timestamp: new Date().toISOString(),
    };

    // Remove undefined values to keep context clean
    Object.keys(enrichedContext).forEach((key) => enrichedContext[key] === undefined && delete enrichedContext[key]);

    datadogRum.addError(error, enrichedContext);
  }

  /**
   * Track a custom action (non-error event worth tracking)
   */
  static trackAction(actionName: string, context: Record<string, any> = {}): void {
    const globalContext = datadogRum.getGlobalContext();

    const enrichedContext = {
      ...context,
      sessionPhase: globalContext.sessionStatus,
      timestamp: new Date().toISOString(),
    };

    datadogRum.addAction(actionName, enrichedContext);
  }

  /**
   * Track a claims-by-policy response that came back short, so these sessions can be found and
   * counted in Datadog. Facet on outcome, which is one of total-failure, partial or call-failed.
   *
   * Each outcome only sends the fields it has. A total failure has no entries, so sending a count
   * there would report zero for the worst case and break any sum over affectedPolicyCount.
   *
   * Affected rather than failed - an ARCHIVE entry means the policy returned its active claims and
   * only the archived ones are missing. The API adds at most one entry per policy, so the count is
   * policies and not entries.
   *
   * Sends system rather than reason. A Workers Comp failure puts the raw upstream error text in
   * reason, which would be unbounded cardinality, whereas system is one of a few fixed values.
   *
   * @param claimsResponse {any} - What getClaims returned
   */
  static trackClaimsRetrievalIssue(claimsResponse: any): void {
    const failureReason = _get(claimsResponse, 'status.failureReason', '');
    const statusCode = _get(claimsResponse, 'statusCode', '');

    // Only the API's 500 carries a failureReason, and it has no entries because every policy failed.
    if (failureReason) {
      DatadogErrorTracker.trackAction(ActionNames.CLAIMS_RETRIEVAL_ISSUE, {
        outcome: 'total-failure',
        statusCode,
        failureReason,
      });
      return;
    }

    const partialFailures = _get(claimsResponse, 'partialFailures', []);
    const entries = Array.isArray(partialFailures) ? partialFailures : [];

    if (entries.length) {
      DatadogErrorTracker.trackAction(ActionNames.CLAIMS_RETRIEVAL_ISSUE, {
        outcome: 'partial',
        affectedSystems: entries.map((f: any) => _get(f, 'system', '')),
        affectedPolicyCount: entries.length,
      });
      return;
    }

    // No failureReason and no entries, so the call itself broke - a rejected fetch, a gateway 5xx,
    // or a body we could not parse. No counts to send, they would all read as zero.
    DatadogErrorTracker.trackAction(ActionNames.CLAIMS_RETRIEVAL_ISSUE, {
      outcome: 'call-failed',
      statusCode,
    });
  }

  /**
   * Set feature context when entering a specific feature area
   */
  static setFeatureContext(featureArea: string, additionalContext: Record<string, any> = {}): void {
    datadogRum.setGlobalContextProperty('currentFeature', featureArea);

    Object.entries(additionalContext).forEach(([key, value]) => {
      datadogRum.setGlobalContextProperty(key, value);
    });
  }

  /**
   * Clear feature context when leaving a feature area
   */
  static clearFeatureContext(keys: string[] = ['currentFeature', 'activeAccordion']): void {
    keys.forEach((key) => {
      datadogRum.removeGlobalContextProperty(key);
    });
  }

  /**
   * Helper to get network status
   */
  static getNetworkStatus(): string {
    return navigator.onLine ? 'online' : 'offline';
  }

  /**
   * Helper to create error object from unknown error
   */
  static normalizeError(error: unknown): Error {
    if (error instanceof Error) {
      return error;
    }
    if (typeof error === 'string') {
      return new Error(error);
    }
    return new Error(JSON.stringify(error));
  }
}
